import { Component } from '@theme/component';
import {
  coverMapper,
  detectProduct,
  dimensionsFromSpecs,
  layoutDimensions,
  plausibleDimensions,
  sideArrowX,
} from '@theme/product-dimensions-layout';

/** Width of the downscaled copy used to find the product silhouette */
const ANALYSIS_WIDTH = 200;

/** Labels keep this distance (px) from the image edges and the toggle */
const EDGE = 6;

/** Remembers a customer who switched the dimensions off */
const STORAGE_KEY = 'fabini:product-dimensions';

/** @type {Map<string, Promise<ReturnType<typeof detectProduct>>>} */
const analysisCache = new Map();

/**
 * Finds the product in the packshot once per image, shared by the desktop and mobile gallery.
 * @param {string} src
 */
function analyzeImage(src) {
  let analysis = analysisCache.get(src);
  if (analysis) return analysis;

  analysis = new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onerror = () => resolve(null);
    image.onload = () => {
      try {
        const width = Math.min(ANALYSIS_WIDTH, image.naturalWidth);
        const height = Math.max(1, Math.round((image.naturalHeight / image.naturalWidth) * width));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return resolve(null);
        context.drawImage(image, 0, 0, width, height);
        resolve(detectProduct(context.getImageData(0, 0, width, height)));
      } catch {
        // Tainted canvas (missing CORS headers) or decoding issue: show nothing
        resolve(null);
      }
    };
    image.src = src;
  });

  analysisCache.set(src, analysis);
  return analysis;
}

function readEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** @param {boolean} enabled */
function storeEnabled(enabled) {
  try {
    if (enabled) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, 'off');
  } catch {
    // Storage blocked: the choice lasts for this page view only
  }
}

const PLATE_ICON =
  '<svg class="product-dimensions-overlay__plate" viewBox="0 0 100 100" focusable="false"><circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" stroke-width="4"/><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="3.6"><circle cx="50" cy="50" r="37" stroke-dasharray="0 6.46"/><circle cx="50" cy="50" r="30" stroke-dasharray="0 6.28"/><circle cx="50" cy="50" r="23" stroke-dasharray="0 6.02"/><circle cx="50" cy="50" r="16" stroke-dasharray="0 6.28"/><circle cx="50" cy="50" r="9" stroke-dasharray="0 5.65"/></g><circle cx="50" cy="50" r="2" fill="currentColor"/></svg>';

/**
 * Draws the product's dimensions (from the specification rows) around the product
 * found on the first gallery image, with a switch to hide them. Stays hidden when
 * there is nothing reliable to show.
 */
class ProductDimensionsOverlay extends Component {
  /** @type {ReturnType<typeof detectProduct>} */
  #product = null;

  /** @type {ReturnType<typeof layoutDimensions> | null} */
  #layout = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Shows or hides the drawing; the switch stays visible.
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.toggleAttribute('data-off', !enabled);
    this.#toggle?.setAttribute('aria-checked', String(enabled));
    if (enabled) this.#position();
  }

  async #init() {
    const scope = this.closest('.shopify-section') ?? document;
    const rows = [...scope.querySelectorAll('.product-content-tabs__specs-row')].map((row) => ({
      key: row.getAttribute('data-spec-key') ?? '',
      label: row.querySelector('.product-content-tabs__specs-label')?.textContent ?? '',
      value: row.querySelector('.product-content-tabs__specs-value')?.textContent ?? '',
    }));

    const dimensions = dimensionsFromSpecs(rows, this.dataset.productType);
    const src = this.dataset.src;
    if (!dimensions.length || !src) return;

    const product = await analyzeImage(src);
    if (!product || !this.isConnected) return;

    const drawn = plausibleDimensions(product, dimensions, Number(this.dataset.aspect) || 1);
    if (!drawn.length) return;

    this.#product = product;
    this.#layout = layoutDimensions(
      product,
      drawn.map(({ role }) => role)
    );
    this.#render(dimensions, drawn);

    // Unhide first so the synchronous measurement sees the real size (positions are set before paint)
    this.hidden = false;
    this.setEnabled(readEnabled());

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#position);
      this.#resizeObserver.observe(this);
    }
  }

  /**
   * @param {ReturnType<typeof dimensionsFromSpecs>} dimensions - all values, for the text summary
   * @param {ReturnType<typeof dimensionsFromSpecs>} drawn - values that fit the photo
   */
  #render(dimensions, drawn) {
    const summary = document.createElement('p');
    summary.className = 'visually-hidden';
    summary.textContent = `${this.dataset.heading ?? ''}: ${dimensions
      .map(({ label, value }) => `${label}: ${value}`)
      .join(', ')}`;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'product-dimensions-overlay__toggle';
    toggle.setAttribute('role', 'switch');
    toggle.setAttribute('aria-checked', 'true');
    toggle.innerHTML = '<span class="product-dimensions-overlay__toggle-track" aria-hidden="true"></span>';
    toggle.prepend(this.dataset.toggleLabel ?? '');
    toggle.addEventListener('click', () => {
      const enabled = this.hasAttribute('data-off');
      storeEnabled(enabled);
      // Desktop gallery and mobile carousel show the same image: keep them in sync
      for (const overlay of document.querySelectorAll('product-dimensions-overlay')) {
        if (overlay instanceof ProductDimensionsOverlay) overlay.setEnabled(enabled);
      }
    });
    this.#toggle = toggle;

    const graphic = document.createElement('div');
    graphic.className = 'product-dimensions-overlay__graphic';
    graphic.setAttribute('aria-hidden', 'true');

    for (const { role, value } of drawn) {
      // No room beside the product: the value stays in the text summary only
      if (!this.#layout?.[role] && role !== 'plateDiameter') continue;

      const item = document.createElement('span');
      item.className = `product-dimensions-overlay__item product-dimensions-overlay__item--${role}`;
      item.dataset.role = role;

      if (role === 'width' || role === 'height' || role === 'depth') {
        const arrow = document.createElement('span');
        arrow.className = 'product-dimensions-overlay__arrow';
        item.append(arrow);
      } else if (role === 'plateDiameter') {
        item.insertAdjacentHTML('beforeend', PLATE_ICON);
      }

      // Compound values ("7 cm | 15 cm s poklicí") go on separate lines
      const text = document.createElement('span');
      text.className = 'product-dimensions-overlay__value';
      for (const part of value.split(/\s*\|\s*/).filter(Boolean)) {
        const line = document.createElement('span');
        line.className = 'product-dimensions-overlay__line';
        line.textContent = role.toLowerCase().includes('diameter') ? `ø ${part}` : part;
        text.append(line);
      }
      item.append(text);
      graphic.append(item);
    }

    this.replaceChildren(summary, graphic, toggle);
  }

  #position = () => {
    const layout = this.#layout;
    const { clientWidth, clientHeight } = this;
    if (!layout || !clientWidth || !clientHeight) return;

    const map = coverMapper(Number(this.dataset.aspect) || 1, clientWidth, clientHeight);
    /** @param {number} value */
    const pct = (value) => `${value.toFixed(2)}%`;

    for (const item of this.querySelectorAll('[data-role]')) {
      if (!(item instanceof HTMLElement)) continue;
      const anchor = layout[item.dataset.role ?? ''];
      if (!anchor) continue;

      item.hidden = false;
      item.removeAttribute('data-label');
      const value = item.querySelector('.product-dimensions-overlay__value');
      if (value instanceof HTMLElement) value.style.translate = '';

      if ('x1' in anchor) {
        item.style.left = pct(map.x(anchor.x1));
        item.style.width = pct(map.x(anchor.x2) - map.x(anchor.x1));
        item.style.top = pct(map.y(anchor.y));
      } else if ('y1' in anchor) {
        this.#placeSideArrow(item, anchor.side, anchor.compact ? 'vertical' : 'beside', map);
        item.style.top = pct(map.y(anchor.y1));
        item.style.height = pct(map.y(anchor.y2) - map.y(anchor.y1));
      } else {
        item.style.left = pct(map.x(anchor.x));
        item.style.top = pct(map.y(anchor.y));
      }
    }

    // Measuring needs the drawing on screen
    if (!this.hasAttribute('data-off')) this.#fit(map);
  };

  /**
   * @param {HTMLElement} item
   * @param {'left' | 'right'} side
   * @param {'beside' | 'vertical' | 'cap'} mode - value beside the arrow, vertical along it, or above its top
   * @param {ReturnType<typeof coverMapper>} map
   */
  #placeSideArrow(item, side, mode, map) {
    if (!this.#product) return;
    item.dataset.side = side;
    item.toggleAttribute('data-compact', mode === 'vertical');
    if (mode === 'cap') item.dataset.label = 'cap';
    else item.removeAttribute('data-label');
    const value = item.querySelector('.product-dimensions-overlay__value');
    if (value instanceof HTMLElement) value.style.translate = '';
    item.style.left = `${map.x(sideArrowX(this.#product.box, side, mode !== 'beside')).toFixed(2)}%`;
  }

  /**
   * Keeps every value inside the image: side arrows try a vertical value, the other
   * side and a value above the arrow, and are dropped as a last resort; centred
   * values shift inwards.
   * @param {ReturnType<typeof coverMapper>} map
   */
  #fit(map) {
    const bounds = this.getBoundingClientRect();
    const toggle = this.#toggle?.getBoundingClientRect();

    /** @param {DOMRect} rect */
    const hitsToggle = (rect) =>
      !!toggle &&
      rect.right > toggle.left - EDGE &&
      rect.left < toggle.right + EDGE &&
      rect.bottom > toggle.top - EDGE &&
      rect.top < toggle.bottom + EDGE;

    /** @param {Element} element */
    const fits = (element) => {
      const rect = element.getBoundingClientRect();
      return (
        rect.left >= bounds.left + EDGE &&
        rect.right <= bounds.right - EDGE &&
        rect.top >= bounds.top + EDGE &&
        rect.bottom <= bounds.bottom - EDGE &&
        !hitsToggle(rect)
      );
    };

    /**
     * Shift an element horizontally into the image; returns the shift in px.
     * @param {HTMLElement} element
     */
    const clampX = (element) => {
      const rect = element.getBoundingClientRect();
      let dx = 0;
      if (rect.left < bounds.left + EDGE) dx = bounds.left + EDGE - rect.left;
      else if (rect.right > bounds.right - EDGE) dx = bounds.right - EDGE - rect.right;
      if (dx) element.style.translate = `${Math.round(dx)}px 0`;
      return dx;
    };

    const sideItems = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-role="height"], [data-role="depth"]'),
    ]);
    for (const item of sideItems) {
      const value = item.querySelector('.product-dimensions-overlay__value');
      if (!(value instanceof HTMLElement) || fits(value)) continue;

      const side = item.dataset.side === 'left' ? 'left' : 'right';
      const other = side === 'right' ? 'left' : 'right';
      const otherFree = !sideItems.some(
        (sibling) => sibling !== item && !sibling.hidden && sibling.dataset.side === other
      );

      // Vertical along the arrow, the free side, then above the arrow's top
      /** @type {Array<['left' | 'right', 'beside' | 'vertical' | 'cap']>} */
      const attempts = [[side, 'vertical']];
      if (otherFree) attempts.push([other, 'beside'], [other, 'vertical']);
      attempts.push([side, 'cap']);
      if (otherFree) attempts.push([other, 'cap']);

      const placed = attempts.some(([nextSide, mode]) => {
        this.#placeSideArrow(item, nextSide, mode, map);
        if (mode === 'cap') clampX(value);
        return fits(value);
      });
      if (!placed) item.hidden = true;
    }

    for (const item of this.querySelectorAll(
      '[data-role="diameter"], [data-role="bottomDiameter"], [data-role="width"]'
    )) {
      if (!(item instanceof HTMLElement)) continue;
      const value = item.querySelector('.product-dimensions-overlay__value');
      if (!(value instanceof HTMLElement)) continue;

      // Width value too low: put it above its arrow
      if (item.dataset.role === 'width' && value.getBoundingClientRect().bottom > bounds.bottom - EDGE) {
        item.dataset.label = 'above';
      }

      const rect = value.getBoundingClientRect();
      let dx = 0;
      let dy = 0;
      if (rect.left < bounds.left + EDGE) dx = bounds.left + EDGE - rect.left;
      else if (rect.right > bounds.right - EDGE) dx = bounds.right - EDGE - rect.right;
      if (rect.top < bounds.top + EDGE) dy = bounds.top + EDGE - rect.top;
      else if (rect.bottom > bounds.bottom - EDGE) dy = bounds.bottom - EDGE - rect.bottom;
      if (toggle && hitsToggle(new DOMRect(rect.left + dx, rect.top + dy, rect.width, rect.height))) {
        dy = toggle.bottom + EDGE - rect.top;
      }
      if (dx || dy) value.style.translate = `${Math.round(dx)}px ${Math.round(dy)}px`;
    }
  }
}

if (!customElements.get('product-dimensions-overlay')) {
  customElements.define('product-dimensions-overlay', ProductDimensionsOverlay);
}
